import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { ReactNode } from 'react';

import { accessGrantKeys, endAccessGrant } from './api';
import { grantStore, type GrantInfo } from './grant-store';

import {
  clearPlatformTenantGetter,
  clearStepUpHandler,
  setPlatformTenantGetter,
  setStepUpHandler,
} from '@/api/client';
import { tenantScopeKey } from '@/api/tenant-keys';
import { useAuth } from '@/auth/AuthProvider';
import { useTenantNames } from '@/hooks/useTenantNames';
import { shortTenantId } from '@/lib/clinic-label';

// Cross-tenant access for System Admins (ADR-0023 §1).
//
// - Opens the step-up dialog when a page or the axios interceptor asks for
//   access to a tenant. One dialog at a time.
// - Ends grants (End now) and forgets the tenant's cached queries when a
//   grant ends, expires, is refused by the server, or on logout.
//
// The grant token never enters React state. Components see `GrantInfo`.

// Lazy: the dialog pulls in the form stack, which the initial bundle
// doesn't need (§1.5).
const StepUpDialog = lazy(() => import('./StepUpDialog'));

interface TenantAccessContextValue {
  grants: readonly GrantInfo[];
  /** Opens the dialog unless a grant is live. Resolves true once one is. */
  requestAccess: (tenantId: string) => Promise<boolean>;
  /** Stops using the grant now and ends it on the server. */
  endAccess: (tenantId: string) => Promise<void>;
  /**
   * "the platform" for the admin's own tenant, else the clinic's name when
   * the list or its summary has loaded, else "clinic 0193a1b2".
   */
  tenantLabel: (tenantId: string) => string;
}

// Outside a provider nothing is granted and nothing can be asked for, so a
// component rendered alone in a spec stays inert instead of crashing.
const INERT: TenantAccessContextValue = {
  grants: [],
  requestAccess: () => Promise.resolve(false),
  endAccess: async () => {},
  tenantLabel: (tenantId) => fallbackTenantLabel(tenantId),
};

const TenantAccessContext = createContext<TenantAccessContextValue>(INERT);

interface PendingStepUp {
  tenantId: string;
  promise: Promise<boolean>;
  resolve: (granted: boolean) => void;
}

interface TenantAccessProviderProps {
  children: ReactNode;
}

export function TenantAccessProvider({ children }: TenantAccessProviderProps): ReactNode {
  const queryClient = useQueryClient();
  const { status, tenantId: ownTenantId } = useAuth();
  const grants = useSyncExternalStore(grantStore.subscribe, grantStore.getSnapshot);

  const pendingRef = useRef<PendingStepUp | null>(null);
  const [dialogTenantId, setDialogTenantId] = useState<string | null>(null);
  const [toForget, setToForget] = useState<readonly string[]>([]);

  const finishStepUp = useCallback((granted: boolean): void => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setDialogTenantId(null);
    if (pending !== null) {
      pending.resolve(granted);
    }
  }, []);

  const requestAccess = useCallback((tenantId: string): Promise<boolean> => {
    const key = tenantId.toLowerCase();
    if (grantStore.get(key) !== null) {
      return Promise.resolve(true);
    }
    const pending = pendingRef.current;
    if (pending !== null) {
      if (pending.tenantId === key) {
        return pending.promise;
      }
      // Another clinic's dialog is open. Don't stack dialogs.
      return Promise.resolve(false);
    }
    let resolve: (granted: boolean) => void = () => {};
    const promise = new Promise<boolean>((done) => {
      resolve = done;
    });
    pendingRef.current = { tenantId: key, promise, resolve };
    setDialogTenantId(key);
    return promise;
  }, []);

  const endAccess = useCallback(async (tenantId: string): Promise<void> => {
    const info = grantStore.get(tenantId);
    grantStore.remove(tenantId, 'ended');
    if (info === null) {
      return;
    }
    try {
      await endAccessGrant(info.tenantId, info.grantId);
    } catch (error) {
      // The grant is already unusable here, and the server ends it at
      // expiry anyway. Status only: never the token or a body.
      console.warn(
        '[ema-web] ending access grant failed',
        (error as { response?: { status?: number } }).response?.status ?? 'no-status',
      );
    }
  }, []);

  // The axios interceptor opens the dialog through this handler.
  useEffect(() => {
    setStepUpHandler(requestAccess);
    return () => {
      clearStepUpHandler();
    };
  }, [requestAccess]);

  // Creating a clinic takes a grant for the caller's own tenant.
  useEffect(() => {
    setPlatformTenantGetter(() => ownTenantId);
    return () => {
      clearPlatformTenantGetter();
    };
  }, [ownTenantId]);

  // Queue the tenant's cached data for removal when its grant goes away.
  useEffect(() => {
    return grantStore.subscribe((event) => {
      if (event.type === 'issued') {
        return;
      }
      setToForget((previous) => [...previous, event.tenantId]);
    });
  }, []);

  // Runs after the render that unmounted the tenant's pages, so removing
  // the entries doesn't make a mounted observer fetch them again (§0.2).
  useEffect(() => {
    if (toForget.length === 0) {
      return;
    }
    for (const tenantId of toForget) {
      const queryKey = tenantScopeKey(tenantId);
      void queryClient.cancelQueries({ queryKey });
      queryClient.removeQueries({ queryKey });
    }
    queryClient.removeQueries({ queryKey: accessGrantKeys.all });
    setToForget([]);
  }, [toForget, queryClient]);

  // Logout or a failed refresh: drop every grant and the whole cache.
  const previousStatus = useRef(status);
  useEffect(() => {
    const was = previousStatus.current;
    previousStatus.current = status;
    if (status === 'unauthenticated' && was === 'authenticated') {
      grantStore.clear();
      finishStepUp(false);
      queryClient.clear();
    }
  }, [status, queryClient, finishStepUp]);

  const tenantName = useTenantNames();
  const tenantLabel = useCallback(
    (tenantId: string): string => {
      if (ownTenantId !== null && tenantId.toLowerCase() === ownTenantId.toLowerCase()) {
        return 'the platform';
      }
      return tenantName(tenantId) ?? fallbackTenantLabel(tenantId);
    },
    [ownTenantId, tenantName],
  );

  const value = useMemo<TenantAccessContextValue>(
    () => ({ grants, requestAccess, endAccess, tenantLabel }),
    [grants, requestAccess, endAccess, tenantLabel],
  );

  return (
    <TenantAccessContext.Provider value={value}>
      {children}
      {dialogTenantId !== null ? (
        <Suspense fallback={null}>
          <StepUpDialog
            tenantId={dialogTenantId}
            tenantLabel={tenantLabel(dialogTenantId)}
            onGranted={() => {
              finishStepUp(true);
            }}
            onCancel={() => {
              finishStepUp(false);
            }}
          />
        </Suspense>
      ) : null}
    </TenantAccessContext.Provider>
  );
}

/** "clinic 0193a1b2": how a clinic is named before its name is known. */
export function fallbackTenantLabel(tenantId: string): string {
  return `clinic ${shortTenantId(tenantId)}`;
}

export function useTenantAccess(): TenantAccessContextValue {
  return useContext(TenantAccessContext);
}

/** The live grant for one tenant, without its token, or null. */
export function useTenantGrant(tenantId: string | undefined): GrantInfo | null {
  const { grants } = useTenantAccess();
  if (tenantId === undefined) {
    return null;
  }
  const key = tenantId.toLowerCase();
  return (
    grants.find((grant) => {
      return grant.tenantId === key;
    }) ?? null
  );
}
